import { useRef, useState } from 'react';
import { Camera, Trash2, ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { toast } from 'sonner';
import { compressImage } from '@/shared/utils/image';

// 1200 px suffisent largement : sur une photo d'etiquette prise au telephone, le numero de
// serie occupe encore environ 840 px de large a cette taille, soit bien plus qu'il n'en
// faut pour le recopier. Mesure faite sur une etiquette reelle : 127 Ko a 1600/85 contre
// 53 Ko a 1200/78, pour une lisibilite inchangee. Une fiche chantier a cinq equipements
// passe ainsi de ~1,3 Mo a ~530 Ko, et ces images voyagent dans le document de l'actif.
// ⚠️ NE PAS LES BAISSER (orchestrateur, 27/10) : la photo sert a LIRE un numero de serie.
// Un test les garde (`PhotoSerialCapture.test.jsx`).
const COTE_MAX_ETIQUETTE = 1200;
const QUALITE_ETIQUETTE = 0.78;

/**
 * Capture de la photo d'étiquette de numéro de série — repris de la version du client.
 *
 * Le poseur photographie l'étiquette avec l'appareil arrière du téléphone
 * (`capture="environment"`), et l'image est redimensionnée à 1200 px puis compressée en
 * JPEG dans le navigateur : elle passe de plusieurs mégaoctets à une centaine de Ko au
 * plus avant même de quitter l'appareil. C'est ce qui rend acceptable de la stocker dans
 * l'actif plutôt que dans un service de fichiers — et c'est nécessaire, parce que la fiche chantier
 * l'affiche dans un `<img src>`, qui ne peut pas porter de jeton d'authentification.
 *
 * Une photo par étiquette attendue : reprendre une photo remplace la précédente au lieu
 * de s'ajouter, sans quoi le SAV se retrouverait devant six clichés du même autocollant
 * sans savoir lequel fait foi.
 *
 * Le serveur borne à son tour le nombre et le poids : cet écran n'est pas le seul chemin
 * vers l'API.
 *
 * DATÉE DANS L'IMAGE, DEPUIS LE 27/10 (lot L5, David 13/10 : « elles sont horodatées ? »).
 * La date d'ajout est gravée par `compressImage`, la même gravure que les photos
 * d'installation : elle se lit donc partout où la photo circule. `taken_at` est le même
 * instant que la date gravée, jamais un second `new Date()`.
 */
export default function PhotoSerialCapture({ photos = [], onChange, labels = ['N° série'], required = true, tid = 'photo-capture' }) {
  const [uploading, setUploading] = useState(false);
  const inputRefs = useRef({});

  const handleFile = async (label, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Fichier image requis'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Image trop lourde (max 15 Mo)'); return; }
    setUploading(true);
    try {
      const { data, taken_at } = await compressImage(file, COTE_MAX_ETIQUETTE, QUALITE_ETIQUETTE);
      const next = [
        ...photos.filter(p => p.label !== label),  // remplace si meme label
        { label, data_uri: data, taken_at },
      ];
      onChange(next);
      toast.success(`Photo « ${label} » ajoutée`);
    } catch {
      toast.error('Erreur lecture image');
    }
    setUploading(false);
  };

  const removePhoto = (label) => {
    onChange(photos.filter(p => p.label !== label));
  };

  return (
    <div className="space-y-2" data-testid={tid}>
      {labels.map((label) => {
        const existing = photos.find(p => p.label === label);
        const inputId = `${tid}-${label.replace(/\s+/g, '-')}`;
        return (
          <div key={label} className="rounded-md border border-dashed border-neutral-300 p-3 bg-neutral-50/40" data-testid={`${tid}-slot-${label.replace(/\s+/g, '-')}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-neutral-500" />
                <span className="text-[12px] font-medium text-neutral-800">
                  Photo — {label}
                  {required && !existing && <span className="text-red-500 ml-1">*</span>}
                </span>
              </div>
              {existing ? (
                <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`${tid}-badge-ok-${label.replace(/\s+/g, '-')}`}>
                  ✓ Prise
                </span>
              ) : required ? (
                <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <AlertCircle className="w-3 h-3" /> Obligatoire
                </span>
              ) : null}
            </div>

            {existing ? (
              <div className="flex items-center gap-3">
                <img src={existing.data_uri} alt={label} className="w-24 h-24 rounded-md object-cover border border-neutral-200" data-testid={`${tid}-thumb-${label.replace(/\s+/g, '-')}`} />
                <div className="flex-1">
                  <p className="text-[10.5px] text-neutral-500">Prise le {new Date(existing.taken_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  <div className="flex gap-2 mt-2">
                    <label htmlFor={inputId} className="cursor-pointer">
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-neutral-300 hover:bg-neutral-100 text-neutral-700">
                        <Camera className="w-3 h-3" /> Reprendre
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => removePhoto(label)}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-red-200 hover:bg-red-50 text-red-600"
                      data-testid={`${tid}-remove-${label.replace(/\s+/g, '-')}`}
                    >
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <label htmlFor={inputId}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full h-11 border-dashed"
                  disabled={uploading}
                  onClick={() => inputRefs.current[label]?.click()}
                  data-testid={`${tid}-capture-${label.replace(/\s+/g, '-')}`}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {uploading ? 'Traitement…' : 'Prendre la photo'}
                </Button>
              </label>
            )}
            <input
              ref={(el) => { inputRefs.current[label] = el; }}
              id={inputId}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(label, e)}
              data-testid={`${tid}-input-${label.replace(/\s+/g, '-')}`}
            />
          </div>
        );
      })}
    </div>
  );
}
